import React, { useMemo, useState } from 'react';
import {
  Divider,
  Select,
  Table,
} from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { FormHelpers } from 'ontraccr-common';

import HoverHelp from '../../common/HoverHelp';
import WorkflowConfigureSourceSelector from './selectors/WorkflowConfigureSourceSelector';
import { getSourceFieldMap, getType } from './workflowHelpers';
import OnTraccrButton from '../../common/buttons/OnTraccrButton';
import BorderlessButton from '../../common/buttons/BorderlessButton';
import colors from '../../constants/Colors';
import { getIdMap } from '../../helpers/helpers';

const {
  getDuplicationEnabledSections,
} = FormHelpers;

const SECTION_MAPPING_HELP = (
  <div style={{ maxWidth: 320 }}>
    Map a duplication-enabled source section to a duplication-enabled target section.
    Each time the source section is duplicated, a matching target section is created
    and the field mappings below are copied for that instance.
    <br />
    <br />
    A target section can only be used in one section mapping.
  </div>
);

// Target section id is the row key, so we create a temporary id for draft rows.
let nextDraftId = 0;
const createDraftRow = () => {
  nextDraftId += 1;
  return {
    targetSectionId: `pending-${nextDraftId}`,
    sourceSectionId: undefined,
    fieldMappings: {},
  };
};

const isDraftTargetId = (targetSectionId) => (
  !targetSectionId || String(targetSectionId).startsWith('pending-')
);

export default function WorkflowSectionMappingConfigure({
  sourceSections = [],
  targetSections = [],
  sectionMappings = {},
  onSectionMappingsChange,
  formId,
  isFieldValid = () => true,
}) {
  const sourceDupSections = useMemo(
    () => getDuplicationEnabledSections(sourceSections),
    [sourceSections],
  );
  const targetDupSections = useMemo(
    () => getDuplicationEnabledSections(targetSections),
    [targetSections],
  );

  const [draftRows, setDraftRows] = useState([]);

  const savedRows = useMemo(
    () => Object.entries(sectionMappings || {})
      .filter(([targetSectionId]) => !isDraftTargetId(targetSectionId))
      .map(([targetSectionId, config = {}]) => ({
        targetSectionId,
        sourceSectionId: config.sourceSectionId,
        fieldMappings: config.fieldMappings || {},
      })),
    [sectionMappings],
  );

  const mappingRows = useMemo(
    () => [...savedRows, ...draftRows],
    [savedRows, draftRows],
  );

  const usedTargetIds = useMemo(
    () => new Set(
      mappingRows
        .map((row) => row.targetSectionId)
        .filter((targetSectionId) => !isDraftTargetId(targetSectionId)),
    ),
    [mappingRows],
  );

  const saveMappings = (nextSavedRows) => {
    const next = {};
    nextSavedRows.forEach((row) => {
      if (isDraftTargetId(row.targetSectionId)) return;
      next[row.targetSectionId] = {
        sourceSectionId: row.sourceSectionId,
        fieldMappings: row.fieldMappings || {},
      };
    });
    onSectionMappingsChange(next, formId);
  };

  const onAddMapping = () => {
    setDraftRows((prev) => [...prev, createDraftRow()]);
  };

  const onRemoveMapping = (targetSectionId) => {
    if (isDraftTargetId(targetSectionId)) {
      setDraftRows((prev) => prev.filter((row) => row.targetSectionId !== targetSectionId));
      return;
    }
    saveMappings(savedRows.filter((row) => row.targetSectionId !== targetSectionId));
  };

  const onChangeRow = (currentTargetId, updates) => {
    const currentDraft = draftRows.find((row) => row.targetSectionId === currentTargetId);
    const isDraft = !!currentDraft || isDraftTargetId(currentTargetId);
    const nextTargetId = updates.targetSectionId ?? currentTargetId;

    if (isDraft) {
      const nextRow = { ...(currentDraft || {}), ...updates, targetSectionId: nextTargetId };

      if (isDraftTargetId(nextTargetId)) {
        setDraftRows((prev) => prev.map((row) => (
          row.targetSectionId === currentTargetId ? nextRow : row
        )));
        return;
      }

      // Is no longer a draft, so save the mapping and remove the draft row.
      saveMappings([...savedRows, nextRow]);
      setDraftRows((prev) => prev.filter((row) => row.targetSectionId !== currentTargetId));
      return;
    }

    saveMappings(savedRows.map((row) => (
      row.targetSectionId === currentTargetId
        ? { ...row, ...updates }
        : row
    )));
  };

  const onInnerFieldMappingsChange = (targetSectionId) => (newFieldMappings) => {
    onChangeRow(targetSectionId, { fieldMappings: newFieldMappings });
  };

  if (!sourceDupSections.length || !targetDupSections.length) {
    return null;
  }

  const canAddMapping = mappingRows.length < targetDupSections.length;
  const sourceDupSectionMap = getIdMap(sourceDupSections);
  const targetDupSectionMap = getIdMap(targetDupSections);

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <b>Duplicated Section Mapping</b>
        <HoverHelp content={SECTION_MAPPING_HELP} />
      </div>
      {mappingRows.map((row) => {
        const sourceSection = sourceDupSectionMap[row.sourceSectionId];
        const targetSection = targetDupSectionMap[row.targetSectionId];

        const sourceFieldMap = getSourceFieldMap(sourceSection ? [sourceSection] : []);
        const targetFields = (targetSection?.fields || []).filter((field) => isFieldValid(field));

        const availableTargets = targetDupSections.filter((section) => (
          section.id === row.targetSectionId || !usedTargetIds.has(section.id)
        ));

        return (
          <div
            key={row.targetSectionId}
            style={{
              border: '1px solid #f0f0f0',
              borderRadius: 4,
              padding: 12,
              marginBottom: 12,
            }}
          >
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
              <div style={{ flex: 1 }}>
                <div style={{ marginBottom: 4 }}>Source Section</div>
                <Select
                  placeholder="Select source section"
                  style={{ width: '100%' }}
                  allowClear
                  value={row.sourceSectionId}
                  onChange={(sourceSectionId) => onChangeRow(row.targetSectionId, {
                    sourceSectionId,
                    fieldMappings: {},
                  })}
                  options={sourceDupSections.map((section) => ({
                    value: section.id,
                    label: section.name,
                  }))}
                />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ marginBottom: 4 }}>Target Section</div>
                <Select
                  placeholder="Select target section"
                  style={{ width: '100%' }}
                  value={targetSection ? row.targetSectionId : undefined}
                  onChange={(targetSectionId) => onChangeRow(row.targetSectionId, {
                    targetSectionId,
                    fieldMappings: {},
                  })}
                  options={availableTargets.map((section) => ({
                    value: section.id,
                    label: section.name,
                  }))}
                />
              </div>
              <BorderlessButton
                iconNode={<DeleteOutlined style={{ color: colors.ONTRACCR_RED }} />}
                onClick={() => onRemoveMapping(row.targetSectionId)}
                style={{ width: 'auto', flexShrink: 0, marginLeft: 'auto' }}
              />
            </div>
            {sourceSection && targetSection && (
              <Table
                style={{ marginTop: 12 }}
                pagination={false}
                size="small"
                rowKey="id"
                dataSource={targetFields}
                columns={[
                  {
                    title: 'Source Field',
                    dataIndex: 'sourceField',
                    width: 350,
                    render: (_, field) => {
                      const type = getType(field);
                      const eligibleSourceFields = sourceFieldMap[type] || [];
                      return (
                        <WorkflowConfigureSourceSelector
                          fieldMappings={row.fieldMappings}
                          value={row.fieldMappings[field.id]}
                          defaultValue={row.fieldMappings[field.id]}
                          onFieldMappingsChange={onInnerFieldMappingsChange(row.targetSectionId)}
                          targetId={field.id}
                          eligibleSourceFields={eligibleSourceFields}
                          formId={formId}
                        />
                      );
                    },
                  },
                  {
                    title: 'Target Field',
                    dataIndex: 'title',
                    render: (_, field) => field.configProps?.title || field.title,
                  },
                ]}
              />
            )}
          </div>
        );
      })}
      <OnTraccrButton
        title="Add Section Mapping"
        icon={<PlusOutlined />}
        onClick={onAddMapping}
        disabled={!canAddMapping}
      />
      <Divider />
    </div>
  );
}
